/* import __COLOCATED_TEMPLATE__ from './via-email-channel.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type Store from '@ember-data/store';
import type { FinSetupBehaviorSaveParams } from 'embercom/lib/operator/fin/types';
import type IntlService from 'embercom/services/intl';
import type { ChannelType } from 'embercom/lib/workflows/fin-workflow-preview';
import type DomainService from 'embercom/services/domain-service';
import type { InterfaceIconName } from '@intercom/pulse/lib/interface-icons';
import type PALETTE from '@intercom/pulse/lib/palette';
import { canUseFinEmailSenderSettings } from 'embercom/lib/fin-email-sender-settings';

interface Args {
  accordion: $TSFixMe;
  ruleset: $TSFixMe;
  partialSave: (params: FinSetupBehaviorSaveParams) => void;
  channelType: ChannelType;
}

export default class ViaEmailChannel extends Component<Args> {
  @service declare appService: AppService;
  @service declare store: Store;
  @service declare intl: IntlService;
  @service declare domainService: DomainService;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    // Load domain data to ensure customBounceSettings is available
    // This prevents flaky behavior on page refresh
    // Only load if customBounceSettings is missing
    if (!this.appService.app.customBounceSettings) {
      this.ensureDomainDataLoaded();
    }
  }

  async ensureDomainDataLoaded() {
    try {
      await this.domainService.load();
    } catch (error) {
      console.error('Failed to load domains from domain service', error);
    }
  }

  get subheader() {
    let allConnected =
      this.isForwardingEnabled && this.isAuthenticationEnabled && this.isReplyAddressInboundEnabled;
    return allConnected
      ? this.intl.t('operator.fin.setup.setup-and-go-live.via-email-channel.subheader-connected')
      : '';
  }

  get defaultEmailAddress() {
    return this.domainService.domains
      .flatMap((domain) => domain.addresses)
      .find((address) => address.isDefaultBrandNotificationEmail);
  }

  get domainFromDefaultEmailAddress() {
    return this.domainService.domains.find((domain) =>
      domain.addresses.any((address) => address.isDefaultBrandNotificationEmail),
    );
  }

  get finEmailAddress() {
    return this.domainService.domains
      .flatMap((domain) => domain.addresses)
      .find((address) => address.id === String(this.appService.app.fin_default_email_address_id));
  }

  get domainFromFinEmailAddress() {
    return this.domainService.domains.find((domain) =>
      domain.addresses.any(
        (address) => address.id === String(this.appService.app.fin_default_email_address_id),
      ),
    );
  }

  get isForwardingEnabled() {
    if (canUseFinEmailSenderSettings(this.appService.app)) {
      return this.finEmailAddress?.forwardingEnabled ?? false;
    }

    return this.defaultEmailAddress?.forwardingEnabled ?? false;
  }

  get isAuthenticationEnabled() {
    if (canUseFinEmailSenderSettings(this.appService.app)) {
      return this.domainFromFinEmailAddress?.isAuthenticated ?? false;
    }

    return this.domainFromDefaultEmailAddress?.isAuthenticated ?? false;
  }

  get isReplyAddressInboundEnabled(): boolean {
    return !(
      !this.appService.app.admin_reply_default_address_id &&
      this.domainService.verifiedAndAuthenticatedCustomEmailAddresses.length === 0
    );
  }

  get isFinDefaultEmailAddressIdSet(): boolean {
    return this.appService.app.hasFinDefaultEmailAddressId;
  }

  get forwardingState() {
    if (this.isForwardingEnabled) {
      return 'done';
    }

    if (canUseFinEmailSenderSettings(this.appService.app)) {
      return this.finEmailAddress?.hasForwardingVerificationAttempt ? 'warning' : 'not-done';
    }

    return this.defaultEmailAddress?.hasForwardingVerificationAttempt ? 'warning' : 'not-done';
  }

  get authenticationState() {
    if (this.isAuthenticationEnabled) {
      return 'done';
    }

    if (canUseFinEmailSenderSettings(this.appService.app)) {
      return this.domainFromFinEmailAddress?.hasAuthenticationAttempt ? 'warning' : 'not-done';
    }

    return this.domainFromDefaultEmailAddress?.hasAuthenticationAttempt ? 'warning' : 'not-done';
  }

  get replyAddressInboundState() {
    if (canUseFinEmailSenderSettings(this.appService.app)) {
      return this.isFinDefaultEmailAddressIdSet ? 'done' : 'not-done';
    }

    return this.isReplyAddressInboundEnabled ? 'done' : 'not-done';
  }

  get showWarning() {
    return this.forwardingState === 'warning' || this.authenticationState === 'warning';
  }

  iconPerState: Record<string, InterfaceIconName> = {
    done: 'rounded-check',
    warning: 'alert-circle',
    'not-done': 'rounded-check',
  };

  iconColorPerState: Record<string, keyof typeof PALETTE> = {
    done: 'success-fill',
    warning: 'notice-container',
    'not-done': 'neutral-border-emphasis',
  };

  get steps(): {
    titleKey: string;
    linkKey: string;
    link: string;
    icon: InterfaceIconName;
    iconColor: keyof typeof PALETTE;
    state: string;
  }[] {
    return [
      {
        titleKey: 'operator.fin.setup.setup-and-go-live.via-email-channel.step-1',
        linkKey: 'operator.fin.setup.setup-and-go-live.via-email-channel.step-1-link',
        link: 'https://www.intercom.com/help/en/articles/6522819-automatically-forward-emails-to-the-inbox',
        icon: this.iconPerState[this.forwardingState],
        iconColor: this.iconColorPerState[this.forwardingState],
        state: this.forwardingState,
      },
      {
        titleKey: 'operator.fin.setup.setup-and-go-live.via-email-channel.step-2',
        linkKey: 'operator.fin.setup.setup-and-go-live.via-email-channel.step-2-link',
        link: 'https://www.intercom.com/help/en/articles/9744849-connect-your-email-support-channel#h_b761c867b8',
        state: this.authenticationState,
        icon: this.iconPerState[this.authenticationState],
        iconColor: this.iconColorPerState[this.authenticationState],
      },
      {
        titleKey: 'operator.fin.setup.setup-and-go-live.via-email-channel.step-3',
        linkKey: 'operator.fin.setup.setup-and-go-live.via-email-channel.step-3-link',
        link: 'https://www.intercom.com/help/en/articles/9356221-deploy-fin-ai-agent-over-email',
        state: this.replyAddressInboundState,
        icon: this.iconPerState[this.replyAddressInboundState],
        iconColor: this.iconColorPerState[this.replyAddressInboundState],
      },
    ];
  }
}
declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Operator::Fin::Setup::Sections::ViaEmailChannel': typeof ViaEmailChannel;
  }
}
